import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { getIsFintoolEmail, getIsSubscribed } from 'containers/authorization/selectors';
import AiSpark from 'assets/svg/AiSpark';
import Chat from './Chat';
import { Container, Navbar, NavLogo, NavLogoIcon, NavTitle, NavBadge, NavActions, NavActionButton } from './styles';

const PANEL_ID = 'ai-search-panel';

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g opacity="0.7">
      <path
        d="M0 2C0 0.895431 0.895431 0 2 0H14C15.1046 0 16 0.895431 16 2V14C16 15.1046 15.1046 16 14 16H2C0.895431 16 0 15.1046 0 14V2Z"
        fill="white"
        fillOpacity="0.01"
      />
      <path
        d="M11.5285 3.52922C11.7889 3.26893 12.2115 3.26889 12.4719 3.52922C12.7318 3.78958 12.732 4.21236 12.4719 4.47258L8.94355 8.0009L12.4719 11.5292C12.7318 11.7896 12.732 12.2124 12.4719 12.4726C12.2117 12.7328 11.7889 12.7325 11.5285 12.4726L8.00019 8.94425L4.47187 12.4726C4.21167 12.7328 3.7889 12.7325 3.52851 12.4726C3.26816 12.2122 3.26816 11.7896 3.52851 11.5292L7.05683 8.0009L3.52851 4.47258C3.26816 4.21223 3.26816 3.78957 3.52851 3.52922C3.78887 3.26893 4.21154 3.26889 4.47187 3.52922L8.00019 7.05754L11.5285 3.52922Z"
        fill="#0A0A0A"
      />
    </g>
  </svg>
);

const AiSearch = ({ isOpened, onClose }) => {
  const { t } = useTranslation('app');
  const isFintoolEmail = useSelector(getIsFintoolEmail);
  const isPremium = useSelector(getIsSubscribed);
  const isAvailable = Boolean(isFintoolEmail || isPremium);
  const { pathname } = useLocation();

  useEffect(() => {
    if (isAvailable && pathname) onClose();
  }, [isAvailable, pathname, onClose]);

  useEffect(() => {
    if (!(isAvailable && isOpened)) return () => {};

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    const handleClickOutside = (e) => {
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      const isInside = path.length ? path.some((node) => node?.id === PANEL_ID) : Boolean(e.target?.closest?.(`#${PANEL_ID}`));

      if (!isInside) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.document.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.document.removeEventListener('click', handleClickOutside);
    };
  }, [isAvailable, isOpened, onClose]);

  if (!isAvailable) return null;

  return (
    <Container id={PANEL_ID} $isOpened={isOpened}>
      <Navbar>
        <NavLogo>
          <NavLogoIcon>
            <AiSpark />
          </NavLogoIcon>
          <NavTitle>{t('Fintool AI')}</NavTitle>
          <NavBadge>{t('Beta')}</NavBadge>
        </NavLogo>
        <NavActions>
          <NavActionButton type="button" onClick={onClose} title="Close" aria-label="Close">
            <CloseIcon />
          </NavActionButton>
        </NavActions>
      </Navbar>
      <Chat />
    </Container>
  );
};

AiSearch.propTypes = {
  isOpened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AiSearch;
